import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { usePost, notify } from '../../../../components/component-items/helpers'
import GenericModal from '../../../../components/component-items/modal'
import GlobalStyles from '../../../../components/component-items/styles'

import type { MerchantUserRoleAssignment, RoleListItem } from '../../../../types/rbac'

interface MerchantAssignRoleModalProps {
  show: boolean
  onHide: () => void
  onSaved: () => void
  userId: number
  existingAssignment: MerchantUserRoleAssignment | null
  availableRoles: RoleListItem[]
  companyId: number | null
  csrf: string
}

export const MerchantAssignRoleModal = ({
  show,
  onHide,
  onSaved,
  userId,
  existingAssignment,
  availableRoles,
  companyId,
  csrf,
}: MerchantAssignRoleModalProps) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens/closes or when editing different assignment
  useEffect(() => {
    if (show) {
      if (existingAssignment) {
        setSelectedRoleId(existingAssignment.role)
      } else {
        setSelectedRoleId('')
      }
    }
  }, [show, existingAssignment])

  const handleSubmit = async () => {
    if (!selectedRoleId) {
      notify({ type: 'error', message: 'Please select a role' })
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        role_id: selectedRoleId,
        company_id: companyId,
      }

      const resp = await usePost(`/api/rbac/users/${userId}/roles/`, payload, csrf, false, true, true, true, false)

      if (resp?.data) {
        notify({ type: 'success', message: existingAssignment ? 'Role assignment updated' : 'Role assigned' })
        onSaved()
      } else {
        const errorMsg = resp?.error?.message || 'Failed to assign role'
        notify({ type: 'error', message: errorMsg })
      }
    } catch (error) {
      notify({ type: 'error', message: 'Failed to assign role' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedRole = availableRoles.find((r) => r.id === selectedRoleId)

  return (
    <GenericModal
      heading={existingAssignment ? 'Edit Role Assignment' : 'Assign Role'}
      show={show}
      onHide={onHide}
      buttons={
        <>
          <GlobalStyles.Button className="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </GlobalStyles.Button>
          <GlobalStyles.Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : existingAssignment ? 'Update' : 'Assign'}
          </GlobalStyles.Button>
        </>
      }
    >
      <Form style={{ margin: '1.5em' }}>
        {/* Role Selection */}
        <Form.Group className="required" style={{ marginBottom: '1.5em' }}>
          <Form.Label>Role</Form.Label>
          <Form.Select
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Select a role...</option>
            {[...availableRoles]
              .sort((a, b) => b.level - a.level)
              .map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                  {role.is_system ? ' (System)' : ''}
                </option>
              ))}
          </Form.Select>
          {selectedRole && (
            <Form.Text className="text-muted" style={{ display: 'block', marginTop: '0.5em' }}>
              {selectedRole.description}
            </Form.Text>
          )}
        </Form.Group>

        {/* Info box */}
        <div
          style={{
            background: '#e3f2fd',
            padding: '1em',
            borderRadius: '4px',
            fontSize: '0.9em',
            color: '#1565c0',
          }}
        >
          <strong>Note:</strong> If the user already has a role for this company, it will be replaced with the new role.
        </div>
      </Form>
    </GenericModal>
  )
}

export default MerchantAssignRoleModal
